import { useState } from 'react';
import { fehlendeFunktion, upgradeAnbieten } from '../billtoro/upgrade';
import { Dialog } from '../components/Dialog';
import { t } from '../i18n';
import { fmtDate } from '../lib/format';
import { DatevDialog } from './DatevDialog';
import { GdpduDialog } from './GdpduDialog';
import { TarifMarke } from './tarifHinweis';
import type { Overview } from './types';

// Export: Zeitraum wählen (nach Rechnungsdatum), dann Excel, CSV, PDF-Übersicht oder alles als ZIP herunterladen,
// z. B. für die eigene Ablage oder zur Weitergabe an die Kanzlei
type Range = { key: string; label: string; from: string; to: string };

const iso = (y: number, m: number, d: number) => new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);
const lastDay = (y: number, m: number) => iso(y, m + 1, 0);

function ranges(today: string): Range[] {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  const pm = m === 1 ? 12 : m - 1;
  const py = m === 1 ? y - 1 : y;
  const q = Math.floor((m - 1) / 3) * 3 + 1;
  return [
    { key: 'monat', label: 'Dieser Monat', from: iso(y, m, 1), to: lastDay(y, m) },
    { key: 'vormonat', label: 'Letzter Monat', from: iso(py, pm, 1), to: lastDay(py, pm) },
    { key: 'quartal', label: 'Dieses Quartal', from: iso(y, q, 1), to: lastDay(y, q + 2) },
    { key: 'jahr', label: `Jahr ${y}`, from: iso(y, 1, 1), to: iso(y, 12, 31) },
    { key: 'vorjahr', label: `Jahr ${y - 1}`, from: iso(y - 1, 1, 1), to: iso(y - 1, 12, 31) },
    { key: 'alles', label: 'Alles', from: '', to: '' },
  ];
}

const FORMATS = [
  { f: 'xlsx', title: 'Excel', text: 'Rechnungsliste mit Beträgen je Steuersatz, dazu Umsätze je Monat' },
  { f: 'csv', title: 'CSV', text: 'Für die Buchhaltung oder Ihr Steuerbüro' },
  { f: 'pdf', title: 'PDF-Übersicht', text: 'Umsätze und Rechnungen zum Ausdrucken' },
  { f: 'zip', title: 'Alles als ZIP', text: 'Alle Rechnungen und Mahnungen als PDF, dazu Liste und Übersicht' },
] as const;
// nur wenn es im Zeitraum E-Rechnungen gibt (sonst ist das ZIP ohne XML)
const ZIP_MIT_XML = 'Alle Rechnungen und Mahnungen als PDF, die E-Rechnungen als XML, dazu Liste und Übersicht';

// DATEV hat einen eigenen Zeitraum und die Angaben des Büros, die Betriebsprüfung ein ganzes Jahr: je ein eigenes
// Fenster statt des Export-Fensters
export function ExportDialog({ data, open, onClose }: { data: Overview; open: boolean; onClose(): void }) {
  const [datev, setDatev] = useState(false);
  const [pruefung, setPruefung] = useState(false);
  return (
    <>
      <ExportFenster
        data={data}
        open={open}
        onClose={onClose}
        onDatev={() => {
          onClose();
          setDatev(true);
        }}
        onPruefung={() => {
          onClose();
          setPruefung(true);
        }}
      />
      <DatevDialog data={data} open={datev} onClose={() => setDatev(false)} />
      <GdpduDialog data={data} open={pruefung} onClose={() => setPruefung(false)} />
    </>
  );
}

function ExportFenster(p: { data: Overview; open: boolean; onClose(): void; onDatev(): void; onPruefung(): void }) {
  const { data, open, onClose, onDatev, onPruefung } = p;
  const all = ranges(data.today);
  const [key, setKey] = useState('monat');
  const [from, setFrom] = useState(all[0]!.from);
  const [to, setTo] = useState(all[0]!.to);

  const pick = (r: Range) => {
    setKey(r.key);
    setFrom(r.from);
    setTo(r.to);
  };
  // Billtoro: DATEV erst ab Start (Excel, CSV, PDF, ZIP und Betriebsprüfung gibt es in jedem Tarif)
  const datevFehlt = fehlendeFunktion(data.tarif, 'datev');
  const imZeitraum = data.items.filter((i) => (!from || i.invoiceDate >= from) && (!to || i.invoiceDate <= to));
  const count = imZeitraum.length;
  const text = (x: (typeof FORMATS)[number]) => (x.f === 'zip' && imZeitraum.some((i) => i.eFormat) ? ZIP_MIT_XML : x.text);
  const bad = !!from && !!to && from > to;
  const url = (f: string) => {
    const q = new URLSearchParams({ format: f });
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    return `/api/rechnungstool/${data.firm.id}/export?${q}`;
  };

  return (
    <Dialog open={open} onClose={onClose} title="Exportieren" className="rt-exp-dlg">
      <div className="rt-chips rt-exp-ranges" role="group" aria-label="Zeitraum">
        {all.map((r) => (
          <button key={r.key} type="button" className={`rt-chip${key === r.key ? ' on' : ''}`} aria-pressed={key === r.key} onClick={() => pick(r)}>
            {r.label}
          </button>
        ))}
      </div>
      <div className="rt-grid rt-grid2">
        <label className="field">
          <span>Von (Rechnungsdatum)</span>
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setKey('');
              setFrom(e.target.value);
            }}
          />
        </label>
        <label className="field">
          <span>Bis</span>
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setKey('');
              setTo(e.target.value);
            }}
          />
        </label>
      </div>
      <p className="muted rt-exp-count">
        {bad
          ? 'Der Anfang liegt nach dem Ende.'
          : `${count === 1 ? '1 Rechnung' : `${count} Rechnungen`} ${from || to ? `${from ? `vom ${fmtDate(from)}` : ''} ${to ? `bis ${fmtDate(to)}` : ''}`.trim() : 'insgesamt'}`}
      </p>
      <div className="rt-exp-list">
        {FORMATS.map((x) =>
          bad ? (
            <span key={x.f} className="rt-exp off" aria-disabled="true">
              <b>{x.title}</b>
              <span>{text(x)}</span>
            </span>
          ) : (
            <a key={x.f} className="rt-exp" href={url(x.f)} download>
              <b>{x.title}</b>
              <span>{text(x)}</span>
            </a>
          ),
        )}
        {datevFehlt ? (
          <button
            type="button"
            className="rt-exp rt-gesperrt"
            disabled={datevFehlt === 'bald'}
            onClick={() => {
              // nur ein Fenster zugleich (components/Dialog.tsx)
              onClose();
              upgradeAnbieten(data.tarif, 'datev');
            }}
          >
            <b>
              DATEV <TarifMarke tarif={data.tarif} fehlt={datevFehlt} />
            </b>
            <span>Buchungen und Kunden für Ihr Büro oder Steuerbüro, zum Einlesen in DATEV</span>
          </button>
        ) : (
          <button type="button" className="rt-exp" onClick={onDatev}>
            <b>DATEV</b>
            <span>Buchungen und Kunden für Ihr Büro oder Steuerbüro, zum Einlesen in DATEV</span>
          </button>
        )}
        <button type="button" className="rt-exp" onClick={onPruefung}>
          <b>{t('Für die Betriebsprüfung')}</b>
          <span>{t('Alle Rechnungsdaten eines Jahres im Format fürs Finanzamt (GDPdU)')}</span>
        </button>
      </div>
      <div className="dlg-actions">
        <button type="button" className="btn" onClick={onClose}>
          Schließen
        </button>
      </div>
    </Dialog>
  );
}
